import { parse } from "graphql";
import { SchemaClient } from "../../../common/GraphqlClient";
import {
  DynamoDbOperations,
  DynamoDbTables,
  Source,
} from "../../../common/Source";
import { EMPTY_VALUE } from "../../../Schema";

export type Floor = {
  id: number;
  floorCode: string;
  siteId: string;
  buildingsId: number;
}

type ListFloorsVariables = {
  siteId: string;
  buildingId: number;
  nextToken?: string;
}

const listFloors: Source<Floor[], ListFloorsVariables> = {
  graphql: parse(`
    query listFloorBySiteIdAndFloorCode(
      $nextToken: String,
      $siteId: String,
      $buildingId: Int
    ) {
      listFloorBySiteIdAndFloorCode(
        limit: 10000,
        filter: { isActive: { eq: true }, buildingsId: { eq: $buildingId } }
        nextToken: $nextToken
        siteId: $siteId
      ) {
        nextToken
        items {
          buildingsId
          floorCode
          siteId
          id
        }
      }
    }
  `),
  dynamodb: {
    operation: DynamoDbOperations.QUERY,
    table: DynamoDbTables.FLOORS,
    index: "FloorBySiteIdAndFloorCode",
    keyCondition: ({ siteId }) => ({
      expression: "#siteId = :siteId",
      names: { "#siteId": "siteId" },
      values: { ":siteId": siteId },
    }),
    // buildingsId is not part of this index's key, so it narrows as a filter — the same
    // split the GraphQL query makes, where siteId is the key argument and buildingsId
    // rides along in `filter`.
    filter: ({ buildingId }) => ({
      expression: "#isActive = :isActive AND #buildingsId = :buildingsId",
      names: { "#isActive": "isActive", "#buildingsId": "buildingsId" },
      values: { ":isActive": true, ":buildingsId": buildingId },
    }),
  },
};

type GetFloorVariables = {
  id: number;
}

const getFloorSource: Source<Floor, GetFloorVariables> = {
  graphql: parse(`
    query getIRFloorMaster($id: Int!) {
      getIRFloorMaster(
        id: $id,
        modelName: "IRFloorMaster"
      ) {
        id
        buildingsId
        floorCode
        siteId
        isActive
      }
    }
  `),
  dynamodb: {
    operation: DynamoDbOperations.GET,
    table: DynamoDbTables.FLOORS,
    key: ({ id }) => ({ modelName: "IRFloorMaster", id }),
  },
};

export const getFloors = async (client: SchemaClient, siteId: string, buildingId: number): Promise<Floor[] | typeof EMPTY_VALUE> => {
  return await client.queryList(listFloors, {siteId, buildingId });
};

export const getFloor = async (client: SchemaClient, id: number): Promise<Floor | typeof EMPTY_VALUE> => {
  return (await client.query(getFloorSource, { id }));
};

export const getAndProcessFloors = async (
  client: SchemaClient,
  siteId: string,
  buildingId: number
): Promise<Floor[]> => {
  const floors = await getFloors(client, siteId, buildingId);
  return floors?.map((floor: Floor) => ({
    id: floor.id,
    floorCode: floor.floorCode,
    siteId: floor.siteId,
    buildingsId: floor.buildingsId
  })) ?? [];
};
